import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Bell, CheckCheck, Gift, MessageCircle, RefreshCw, ShieldCheck, Ticket, Wallet } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
  type NotificationsResponse,
} from '../../features/notifications/api';
import { notificationLink, routeFromLink } from '../../features/notifications/notificationRoute';
import { useAuthStore } from '../../features/auth/authStore';
import { FeedbackCompose } from '../../features/support/feedback/FeedbackCompose';
import { FeedbackPanel } from '../../features/support/feedback/FeedbackPanel';
import { HelpPanel } from '../../features/support/HelpPanel';
import { fetchFeedbackSummary } from '../../features/support/feedback/api';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { localClock, localDayShort } from '../../shared/lib/datetime';
import { avisosViewForRoute, clampAvisosViewForGuest, groupNoticesByDay, type AvisosView } from './avisosView';

export function AvisosPage() {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const route = useAppStore((state) => state.route);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveRoomId = useAppStore((state) => state.setActiveRoomId);
  const setActiveOrderId = useAppStore((state) => state.setActiveOrderId);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  const setActiveArchiveRoundNumber = useAppStore((state) => state.setActiveArchiveRoundNumber);
  const setWalletTargetAction = useAppStore((state) => state.setWalletTargetAction);
  const setMeSecurityTargetChannel = useAppStore((state) => state.setMeSecurityTargetChannel);
  const setWalletTicketsTarget = useAppStore((state) => state.setWalletTicketsTarget);
  const openWalletTicketsFlow = useAppStore((state) => state.openWalletTicketsFlow);
  const avisosTargetView = useAppStore((state) => state.avisosTargetView);
  const setAvisosTargetView = useAppStore((state) => state.setAvisosTargetView);
  const activeFeedbackId = useAppStore((state) => state.activeFeedbackId);
  const setActiveFeedbackId = useAppStore((state) => state.setActiveFeedbackId);
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const [view, setView] = useState<AvisosView>(() => avisosViewForRoute(route, guestMode));
  const queryClient = useQueryClient();
  const publicFaqOnly = !token && !guestMode && (route === 'avisos.faq' || route === 'avisos.help');
  const [actionMessage, setActionMessage] = useState('');
  // Owned here, not by FeedbackPanel: Task 16's compose screen replaces the
  // Feedback tab's list in place, the same way FeedbackPanel already
  // replaces itself with FeedbackDetail for a selected thread.
  const [composingFeedback, setComposingFeedback] = useState(false);

  // guestMode is a dependency, not just route: a guest session restored from
  // storage after this component already mounted (or a route restored while
  // guestMode was still hydrating) must still clamp to Help. See
  // avisosViewForRoute for why this is the only place that decides.
  useEffect(() => {
    setView(avisosViewForRoute(route, guestMode));
  }, [route, guestMode]);

  // Leaving the Feedback tab (another tab tap, a deep link, guestMode
  // flipping true mid-session) must not leave the compose screen mounted
  // underneath whatever renders next time this tab is reopened.
  useEffect(() => {
    if (view !== 'feedback') setComposingFeedback(false);
  }, [view]);

  useEffect(() => {
    if (!avisosTargetView) return;
    // Clamp here too: an external caller (today only MePage, targeting
    // 'help') hands over a target tab directly, bypassing the route. Nothing
    // stops a future caller from targeting 'feedback' or 'avisos' — if one
    // ever does while guestMode is true, this must still land on Help rather
    // than trusting the caller.
    setView(clampAvisosViewForGuest(avisosTargetView, guestMode));
    setAvisosTargetView(null);
  }, [avisosTargetView, guestMode, setAvisosTargetView]);

  // Belt-and-suspenders for a deep link that hands over a specific thread:
  // the effect above already flips `view` for any 'avisos.feedback' route
  // change, but this covers the case where the route is already
  // 'avisos.feedback' and only `activeFeedbackId` changes afterwards (e.g. a
  // future notification tap while already on this tab). FeedbackPanel itself
  // is what actually opens the thread; this only makes sure the tab is showing.
  useEffect(() => {
    if (route === 'avisos.feedback' && activeFeedbackId) setView('feedback');
  }, [route, activeFeedbackId]);

  const notificationsQuery = useQuery({
    queryKey: ['notifications', token],
    queryFn: () => fetchNotifications(token),
    enabled: !!token && !guestMode,
  });

  // Drives the Feedback tab's unread dot. FeedbackPanel/FeedbackDetail
  // invalidate this same query key after a successful mark-read, so the tab
  // badge and the list row's dot always clear together.
  const feedbackSummaryQuery = useQuery({
    queryKey: ['feedback-summary', token],
    queryFn: () => fetchFeedbackSummary(token || ''),
    enabled: !!token && !guestMode,
  });
  const feedbackUnread = feedbackSummaryQuery.data?.unread ?? 0;

  const notifications = useMemo(
    () => normalizeNotifications(notificationsQuery.data?.items || []),
    [notificationsQuery.data?.items],
  );
  const unreadCount = notifications.filter((item) => !item.read).length;
  // "Today" is fixed at render, not re-derived per row: a list that straddles
  // midnight must not label two adjacent rows from the same day differently.
  const noticeGroups = useMemo(
    () => groupNoticesByDay(notifications, Date.now() / 1000),
    [notifications],
  );

  const markOneMutation = useMutation({
    mutationFn: (id: number | string) => markNotificationRead(token, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', token] });
      const previous = queryClient.getQueryData<NotificationsResponse>(['notifications', token]);
      queryClient.setQueryData<NotificationsResponse>(['notifications', token], markLocalRead(previous, id));
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(['notifications', token], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications', token] }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(token),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', token] });
      const previous = queryClient.getQueryData<NotificationsResponse>(['notifications', token]);
      queryClient.setQueryData<NotificationsResponse>(['notifications', token], markLocalAllRead(previous));
      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(['notifications', token], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications', token] }),
  });

  function handleNotificationTap(item: UiNotification) {
    setActionMessage('');
    if (!item.read) markOneMutation.mutate(item.id);
    const target = routeFromLink(item.link);
    if (!target) {
      if (item.link) setActionMessage(t('avisosActionUnavailable'));
      return;
    }
    // A raffle result opens as a modal over Updates — don't navigate, so closing
    // returns here instead of dumping the user on the Draws page.
    if (target.walletTicketsView === 'result') {
      openWalletTicketsFlow('result', target.walletTicketsResultId ?? null);
      return;
    }
    if (target.feedbackId) setActiveFeedbackId(target.feedbackId);
    if (target.roomId) setActiveRoomId(target.roomId);
    if (target.orderId) setActiveOrderId(target.orderId);
    if (target.profileUserId) setActiveProfileUserId(target.profileUserId);
    if (target.archiveRoundNumber) setActiveArchiveRoundNumber(target.archiveRoundNumber);
    if (target.walletAction) setWalletTargetAction(target.walletAction);
    if (target.securityChannel) setMeSecurityTargetChannel(target.securityChannel);
    if (target.walletTicketsView) setWalletTicketsTarget(target.walletTicketsView, target.walletTicketsResultId);
    setRoute(target.route);
  }

  return (
    <section className="tab-page avisos-page">
      <header className="tab-page-header">
        {/* Help and FAQ are public routes; the lobby is not. Sending a signed-out
            visitor to 'lobby' handed them straight to App's auth guard, which
            replaced it with the login screen — so "Back to lobby" actually meant
            "sign in", after a frame of lobby and an extra history entry. Send
            them back where they came from, and label the button honestly. */}
        <button className="icon-button" type="button" onClick={() => setRoute(publicFaqOnly ? 'auth.login' : 'lobby')} aria-label={publicFaqOnly ? t('back') : t('backToLobby')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div>
          <p className="eyebrow">{t('avisosEyebrow')}</p>
          <h1>{t('avisos')}</h1>
        </div>
        {/* Quiet on purpose. As a bordered `.ghost-action` chip it carried the
            same visual weight as the page title next to it, so the loudest thing
            on a notifications screen was the button that clears them. */}
        {!guestMode && view === 'avisos' && unreadCount > 0 ? (
          <button className="ghost-action avisos-markall" type="button" onClick={() => markAllMutation.mutate()} disabled={markAllMutation.isPending}>
            <CheckCheck size={14} aria-hidden="true" />
            {t('avisosMarkAllRead')}
          </button>
        ) : null}
      </header>

      {!guestMode && !publicFaqOnly ? (
        <nav className="avisos-tabs" aria-label={t('avisos')}>
          <button type="button" className={view === 'avisos' ? 'active' : ''} onClick={() => { setView('avisos'); if (route !== 'avisos') setRoute('avisos'); }}>
            {t('avisosTab')}
            {unreadCount > 0 ? <i aria-hidden="true" /> : null}
          </button>
          <button type="button" className={view === 'feedback' ? 'active' : ''} onClick={() => { setView('feedback'); if (route !== 'avisos.feedback') setRoute('avisos.feedback'); }}>
            {t('feedbackTab')}
            {feedbackUnread > 0 ? <i aria-hidden="true" /> : null}
          </button>
          <button type="button" className={view === 'help' ? 'active' : ''} onClick={() => { setView('help'); if (route !== 'avisos.help' && route !== 'avisos.faq') setRoute('avisos.help'); }}>
            {t('helpTab')}
          </button>
        </nav>
      ) : null}

      <section className="avisos-panel">
        {/*
          No `!guestMode` check per branch on purpose: avisosViewForRoute
          already guarantees `view` can only be 'feedback' or 'avisos' when
          guestMode is false, so a guest always falls through to HelpPanel
          below regardless of which of these two conditions is checked first.
        */}
        {view === 'feedback' ? (
          composingFeedback ? (
            <FeedbackCompose
              onDone={() => setComposingFeedback(false)}
              onCancel={() => setComposingFeedback(false)}
            />
          ) : (
            <FeedbackPanel onCompose={() => setComposingFeedback(true)} />
          )
        ) : view === 'avisos' ? (
          <>
            {actionMessage ? (
              <p className="avisos-action-feedback" data-avisos-action-feedback-react="true">
                {actionMessage}
              </p>
            ) : null}
            {notificationsQuery.isLoading ? <AvisosState title={t('loading')} /> : null}
            {notificationsQuery.isError ? (
              <AvisosState
                icon="error"
                title={t('avisosLoadError')}
                body={t('avisosLoadErrorBody')}
                actionLabel={t('retry')}
                onAction={() => notificationsQuery.refetch()}
              />
            ) : null}
            {!notificationsQuery.isLoading && !notificationsQuery.isError && notifications.length === 0 ? (
              <AvisosState icon="empty" title={t('avisosEmpty')} body={t('avisosEmptyBody')} />
            ) : null}
            {noticeGroups.map((group) => (
              <Fragment key={group.dayKey}>
                <h2 className="avisos-daygroup">
                  {group.label === 'today' ? t('avisosToday')
                    : group.label === 'yesterday' ? t('avisosYesterday')
                      : localDayShort(group.items[0].ts)}
                </h2>
                {group.items.map((item) => (
                  <NotificationRow key={item.id} item={item} onClick={() => handleNotificationTap(item)} />
                ))}
              </Fragment>
            ))}
          </>
        ) : (
          <HelpPanel onGoToFeedback={() => { setView('feedback'); setRoute('avisos.feedback'); }} />
        )}
      </section>
    </section>
  );
}

type UiNotification = {
  id: number | string;
  type: string;
  title: string;
  body: string;
  ts: number;
  read: boolean;
  link: string;
};

function NotificationRow({ item, onClick }: { item: UiNotification; onClick: () => void }) {
  const t = useI18nStore((state) => state.t);
  const Icon = iconFor(item.type);
  return (
    <button className={`notification-row tone-${toneFor(item.type)} ${item.read ? '' : 'unread'}`} type="button" onClick={onClick}>
      <span className="notification-icon"><Icon size={18} aria-hidden="true" /></span>
      <span className="notification-main">
        <span className="notification-meta">
          {typeLabel(item.type, t)}
          {!item.read ? <i /> : null}
        </span>
        <strong>{item.title || t('avisosUntitled')}</strong>
        <small>{item.body}</small>
        {/* Clock only — the day heading above already said which day, and this
            row used to repeat "22/08, 12:05 AM" on every single line. 24h to
            match the rest of the app. */}
        <em>{localClock(item.ts)}</em>
      </span>
    </button>
  );
}

function AvisosState({ title, body, icon, actionLabel, onAction }: { title: string; body?: string; icon?: 'error' | 'empty'; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="avisos-state">
      {icon === 'error' ? <AlertTriangle size={28} aria-hidden="true" /> : icon === 'empty' ? <Bell size={28} aria-hidden="true" /> : <RefreshCw size={28} aria-hidden="true" />}
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {actionLabel && onAction ? <button type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

function normalizeNotifications(items: NotificationItem[]): UiNotification[] {
  return items.map((item) => ({
    id: item.id,
    type: item.type || 'team_announcement',
    title: item.title || '',
    body: item.body || '',
    ts: Number(item.ts || item.created_at || 0),
    read: !!item.read,
    link: notificationLink(item),
  }));
}

function markLocalRead(previous: NotificationsResponse | undefined, id: number | string): NotificationsResponse | undefined {
  if (!previous) return previous;
  const items = (previous.items || []).map((item) => item.id === id ? { ...item, read: true } : item);
  return { ...previous, unread: items.filter((item) => !item.read).length, items };
}

function markLocalAllRead(previous: NotificationsResponse | undefined): NotificationsResponse | undefined {
  if (!previous) return previous;
  return { ...previous, unread: 0, items: (previous.items || []).map((item) => ({ ...item, read: true })) };
}

/* The categories below must stay in step with NotificationController's
   `notificationType` — the client silently renders anything it does not
   recognise as a team announcement, so a mismatch is invisible rather than
   loud. It was: the server sends `bet`, this file matched `draw_settle`
   (which the server never sent), so every bet notice — placed, won, refunded —
   came through as a grey TEAM shield, and a win looked exactly like a loss.
   NotificationTypeContractTest pins the two lists together. */
function iconFor(type: string) {
  if (type === 'payout') return Wallet;
  if (type === 'maintenance') return AlertTriangle;
  if (type === 'private') return MessageCircle;
  if (type === 'bet') return Ticket;
  if (type === 'raffle') return Gift;
  return ShieldCheck;
}

function toneFor(type: string) {
  if (type === 'payout') return 'green';
  if (type === 'maintenance') return 'amber';
  if (type === 'private') return 'blue';
  if (type === 'bet') return 'teal';
  if (type === 'raffle') return 'gold';
  return 'slate';
}

function typeLabel(type: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (type === 'payout') return t('notifyType_payout');
  if (type === 'maintenance') return t('notifyType_maintenance');
  if (type === 'private') return t('notifyType_private');
  if (type === 'bet') return t('notifyType_bet');
  if (type === 'raffle') return t('notifyType_raffle');
  return t('notifyType_team_announcement');
}
